/**
 * Modal para cambiar el estado de un miembro con historial
 */

import { useState } from "react";
import { Button, Badge, Select } from "@/components/ui";
import {
  X,
  History,
  User,
  Calendar,
  MessageSquare,
  Loader2,
} from "lucide-react";
import {
  useUpdateMemberStatusWithHistory,
  useMemberStatusHistory,
} from "@/hooks/usePersons";
import { useAuthStore } from "@/stores/authStore";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import type { Member, MemberStatus } from "@/types";
import { Timestamp } from "firebase/firestore";

interface StatusChangeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: Member | null;
  personName: string;
}

const STATUS_OPTIONS: { value: MemberStatus; label: string; color: string }[] =
  [
    { value: "active", label: "Activo", color: "bg-green-100 text-green-800" },
    {
      value: "inactive",
      label: "Inactivo",
      color: "bg-gray-100 text-gray-800",
    },
    {
      value: "pending",
      label: "Pendiente",
      color: "bg-yellow-100 text-yellow-800",
    },
    {
      value: "transferred",
      label: "Trasladado",
      color: "bg-blue-100 text-blue-800",
    },
  ];

function getStatusLabel(status: MemberStatus): string {
  return STATUS_OPTIONS.find((s) => s.value === status)?.label || status;
}

function getStatusColor(status: MemberStatus): string {
  return (
    STATUS_OPTIONS.find((s) => s.value === status)?.color ||
    "bg-gray-100 text-gray-800"
  );
}

function formatTimestamp(timestamp: Timestamp | Date | undefined): string {
  if (!timestamp) return "N/A";
  const date = timestamp instanceof Timestamp ? timestamp.toDate() : timestamp;
  return format(date, "d 'de' MMMM, yyyy 'a las' HH:mm", { locale: es });
}

export function StatusChangeModal({
  open,
  onOpenChange,
  member,
  personName,
}: StatusChangeModalProps) {
  const { user } = useAuthStore();
  const [newStatus, setNewStatus] = useState<MemberStatus | "">("");
  const [reason, setReason] = useState("");
  const [showHistory, setShowHistory] = useState(false);

  const updateStatusMutation = useUpdateMemberStatusWithHistory();
  const { data: statusHistory = [], isLoading: loadingHistory } =
    useMemberStatusHistory(member?.id || "");

  const handleSubmit = async () => {
    if (!member || !newStatus || !user?.uid) return;

    await updateStatusMutation.mutateAsync({
      id: member.id,
      status: newStatus,
      reason: reason.trim() || undefined,
      userId: user.uid,
    });

    // Reset form
    setNewStatus("");
    setReason("");
    onOpenChange(false);
  };

  const handleClose = () => {
    setNewStatus("");
    setReason("");
    setShowHistory(false);
    onOpenChange(false);
  };

  if (!open || !member) return null;

  const availableStatuses = STATUS_OPTIONS.filter(
    (s) => s.value !== member.status,
  );

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center shadow-md shadow-primary-500/25">
              <User className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-lg font-semibold">Cambiar Estado de Miembro</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Nombre del miembro */}
          <p className="text-gray-600">{personName}</p>

          {/* Estado actual */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
            <span className="text-sm font-medium">Estado actual:</span>
            <Badge className={getStatusColor(member.status)}>
              {getStatusLabel(member.status)}
            </Badge>
          </div>

          {/* Selector de nuevo estado */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Nuevo estado
            </label>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value as MemberStatus)}
            >
              <option value="">Seleccionar nuevo estado</option>
              {availableStatuses.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </Select>
          </div>

          {/* Razón del cambio */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Razón del cambio <span className="text-gray-400">(opcional)</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ej: Solicitud del miembro, falta de asistencia, traslado a otro club..."
              rows={3}
              className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300"
            />
          </div>

          {/* Toggle historial */}
          <Button
            type="button"
            variant="secondary"
            onClick={() => setShowHistory(!showHistory)}
            className="w-full flex items-center justify-center gap-2"
          >
            <History className="h-4 w-4" />
            {showHistory ? "Ocultar historial" : "Ver historial de cambios"}
          </Button>

          {/* Historial de cambios */}
          {showHistory && (
            <div className="border-2 border-gray-100 rounded-xl">
              <div className="p-3 border-b bg-gray-50">
                <h4 className="font-medium text-sm">Historial de Estados</h4>
              </div>
              <div className="max-h-48 overflow-y-auto">
                {loadingHistory ? (
                  <div className="flex items-center justify-center p-6">
                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                  </div>
                ) : statusHistory.length === 0 ? (
                  <div className="p-6 text-center text-gray-500 text-sm">
                    No hay historial de cambios de estado
                  </div>
                ) : (
                  <div className="divide-y">
                    {statusHistory.map((change) => (
                      <div key={change.id} className="p-3 space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Badge
                            className={`${getStatusColor(change.previousStatus)} border`}
                          >
                            {getStatusLabel(change.previousStatus)}
                          </Badge>
                          <span className="text-gray-400">→</span>
                          <Badge className={getStatusColor(change.newStatus)}>
                            {getStatusLabel(change.newStatus)}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatTimestamp(change.effectiveDate)}
                          </span>
                        </div>
                        {change.reason && (
                          <div className="flex items-start gap-1 text-xs text-gray-500">
                            <MessageSquare className="h-3 w-3 mt-0.5" />
                            <span>{change.reason}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!newStatus || updateStatusMutation.isPending}
            className="flex items-center gap-2"
          >
            {updateStatusMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Guardando...
              </>
            ) : (
              "Cambiar Estado"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
